import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, Publish, Visibility } from '@mui/icons-material';
import api from '../api';
import { useStaff } from '../contexts/StaffContext';
import Spinner from '../components/Spinner';

interface Assignment {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  instructions: string;
  totalPoints: number;
  dueDate: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentUpload?: {
    data?: string;
    mimeType?: string;
    filename?: string;
    originalName?: string;
  };
  isPublished: boolean;
  isVisible: boolean;
  createdAt: string;
}

const StaffAssignments: React.FC = () => {
  const { staff } = useStaff();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [subjects, setSubjects] = useState<Array<{ _id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subjectId: '',
    className: '',
    instructions: '',
    attachmentUrl: '',
    attachmentName: '',
    attachmentUpload: null as null | { data: string; mimeType: string; filename: string; originalName: string },
    totalPoints: 100,
    dueDate: '',
    dueTime: ''
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Clear cache when staff member logs out or switches accounts
  const clearAssignmentsCache = useCallback(() => {
    setAssignments([]);
    setEditingAssignment(null);
    setError(null);
    setSuccess(null);
    setOpenDialog(false);
  }, []);

  // Clear cache when staff context changes (logout or user switch)
  useEffect(() => {
    if (!staff) {
      clearAssignmentsCache();
    }
  }, [staff, clearAssignmentsCache]);

  useEffect(() => {
    loadAssignments();
    loadSubjects();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/staff/assignments');
      setAssignments(response.data);
    } catch (err: any) {
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const response = await api.get('/staff/subjects');
      setSubjects(response.data);
    } catch {
      setSubjects([]);
    }
  };

  const handleAttachmentFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setAttachmentFile(null);
      setFormData((current) => ({ ...current, attachmentUpload: null }));
      return;
    }

    const allowedTypes = ['application/pdf', 'text/plain', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Attachment must be a PDF, TXT, or PNG file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        const base64Data = reader.result.split(',')[1] || '';
        setFormData((current) => ({
          ...current,
          attachmentUpload: {
            data: base64Data,
            mimeType: file.type,
            filename: file.name,
            originalName: file.name
          }
        }));
        setAttachmentFile(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOpenDialog = (assignment?: Assignment) => {
    if (assignment) {
      setEditingAssignment(assignment);
      setAttachmentFile(null);
      setFormData({
        title: assignment.title,
        description: assignment.description,
        subjectId: assignment.subject._id,
        className: assignment.className,
        instructions: assignment.instructions,
        attachmentUrl: assignment.attachmentUrl || '',
        attachmentName: assignment.attachmentName || '',
        attachmentUpload: assignment.attachmentUpload ? {
          data: assignment.attachmentUpload.data || '',
          mimeType: assignment.attachmentUpload.mimeType || 'application/octet-stream',
          filename: assignment.attachmentUpload.filename || '',
          originalName: assignment.attachmentUpload.originalName || ''
        } : null,
        totalPoints: assignment.totalPoints,
        dueDate: assignment.dueDate?.split('T')[0] || '',
        dueTime: assignment.dueDate?.split('T')[1]?.slice(0, 5) || '23:59'
      });
    } else {
      setEditingAssignment(null);
      setAttachmentFile(null);
      setFormData({
        title: '',
        description: '',
        subjectId: '',
        className: '',
        instructions: '',
        attachmentUrl: '',
        attachmentName: '',
        attachmentUpload: null,
        totalPoints: 100,
        dueDate: '',
        dueTime: '23:59'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAssignment(null);
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!formData.title || !formData.subjectId || !formData.dueDate || !formData.dueTime) {
        setError('Title, subject, due date, and due time are required');
        return;
      }

      const dueDateValue = `${formData.dueDate}T${formData.dueTime}:00`;
      const payload = {
        title: formData.title,
        description: formData.description,
        subjectId: formData.subjectId,
        className: formData.className,
        instructions: formData.instructions,
        attachmentUrl: formData.attachmentUrl,
        attachmentName: formData.attachmentName,
        attachmentUpload: formData.attachmentUpload,
        totalPoints: formData.totalPoints,
        dueDate: dueDateValue
      };

      if (editingAssignment) {
        await api.put(`/staff/assignments/${editingAssignment._id}`, payload);
        setSuccess('Assignment updated successfully');
      } else {
        await api.post('/staff/assignments', payload);
        setSuccess('Assignment created successfully');
      }

      handleCloseDialog();
      loadAssignments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save assignment');
    }
  };

  const handlePublish = async (assignmentId: string, currentStatus: boolean) => {
    try {
      await api.put(`/staff/assignments/${assignmentId}/publish`, { isPublished: !currentStatus });
      setSuccess(`Assignment ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      loadAssignments();
    } catch (err: any) {
      setError('Failed to update assignment status');
    }
  };

  const handleDelete = async (assignmentId: string) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      try {
        await api.delete(`/staff/assignments/${assignmentId}`);
        setSuccess('Assignment deleted successfully');
        loadAssignments();
      } catch (err: any) {
        setError('Failed to delete assignment');
      }
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Student Assignments</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Create Assignment
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Subject</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Class</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Due Date</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Points</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length > 0 ? (
              assignments.map((assignment) => (
                <TableRow key={assignment._id}>
                  <TableCell sx={{ color: 'text.primary' }}>{assignment.title}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{assignment.subject?.name}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{assignment.className || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    {assignment.dueDate ? (assignment.dueDate.includes('T') ? new Date(assignment.dueDate).toLocaleString() : new Date(assignment.dueDate).toLocaleDateString()) : 'No deadline'}
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{assignment.totalPoints}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    <Chip
                      label={assignment.isPublished ? 'Published' : 'Draft'}
                      color={assignment.isPublished ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handlePublish(assignment._id, assignment.isPublished)}
                      title={assignment.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      {assignment.isPublished ? <Publish fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(assignment)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(assignment._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                  No assignments created yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAssignment ? 'Edit Assignment' : 'Create New Assignment'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Assignment Title"
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subjectId}
              label="Subject"
              onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject._id} value={subject._id}>
                  {subject.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Class</InputLabel>
            <Select
              value={formData.className}
              label="Class"
              onChange={(e) => setFormData({ ...formData, className: e.target.value })}
            >
              {staff?.className ? (
                <MenuItem value={staff.className}>{staff.className}</MenuItem>
              ) : (
                <MenuItem value="">No class assigned</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            label="Instructions"
            fullWidth
            multiline
            rows={3}
            value={formData.instructions}
            onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
            margin="normal"
          />
          <input
            id="assignment-attachment-upload"
            type="file"
            accept=".pdf,.txt,.png"
            style={{ display: 'none' }}
            onChange={handleAttachmentFileChange}
          />
          <Button
            variant="outlined"
            component="label"
            htmlFor="assignment-attachment-upload"
            sx={{ mt: 2, mb: 1 }}
          >
            {attachmentFile ? 'Change Attachment' : 'Upload Attachment'}
          </Button>
          {attachmentFile ? (
            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
              Selected file: {attachmentFile.name}
            </Typography>
          ) : formData.attachmentUpload ? (
            <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
              Existing attachment: {formData.attachmentUpload.originalName || formData.attachmentUpload.filename}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Acceptable formats: PDF, TXT, PNG. This upload is stored with the assignment.
          </Typography>
          <TextField
            label="Attachment URL (optional)"
            fullWidth
            value={formData.attachmentUrl}
            onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
            margin="normal"
          />
          <TextField
            label="Total Points"
            type="number"
            fullWidth
            value={formData.totalPoints}
            onChange={(e) => {
              const points = parseInt(e.target.value, 10);
              setFormData({ ...formData, totalPoints: Number.isFinite(points) ? points : 0 });
            }}
            margin="normal"
          />
          <TextField
            label="Due Date"
            type="date"
            fullWidth
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Due Time"
            type="time"
            fullWidth
            value={formData.dueTime}
            onChange={(e) => setFormData({ ...formData, dueTime: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingAssignment ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffAssignments;
